import { mdiMagnify, mdiTextSearch } from '@mdi/js';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import { BaseInput } from '../base/input/input';
import { FileCabinetFtsBuilder } from './file-cabinet-fts-builder';
import * as S from './file-cabinet.styles';

/**
 * The search box. What it searches is the store's `searchDef` — the view's own search, or what the
 * source can do instead (the demo has no full text).
 *
 * - **Full text (OCR, transcripts) searches on Enter.** Typed live, every pause would query the OCR
 *   index with a half-typed word, and in boolean mode `den` matches "den", not "denial" — a confident
 *   "No results" for a search the user never finished. The box keeps a draft until Enter.
 * - **Filename and claim ID filter as typed**, like every other table's search.
 *
 * Full text also gets the query builder beside the box; its chips sit under the toolbar
 * (`FileCabinetFtsStrip`).
 */
export const FileCabinetSearch = observer(
    ({ store }: { store: FileCabinetStore }): React.ReactElement => {
        const search = store.searchDef;
        const fullText = search.mode === 'fts' || search.mode === 'transcript';
        const [draft, setDraft] = useState(store.search);
        // The store clears the search on a tab switch; the draft follows.
        useEffect(() => setDraft(store.search), [store.search]);

        const value = fullText ? draft : store.search;
        const pending = fullText && draft.trim() !== store.search.trim();
        const change = (text: string): void => (fullText ? setDraft(text) : store.setSearch(text));
        const clear = (): void => {
            setDraft('');
            store.setSearch('');
        };

        return (
            <S.SearchBlock>
                {/* What is typed here can be a patient's name: blocked from session replay. */}
                <S.Search title={fullText ? 'Full-text search: +must -not "exact phrase" prefix*' : undefined} data-sentry-block>
                    <BaseInput
                        type="search"
                        compact={true}
                        iconPath={fullText ? mdiTextSearch : mdiMagnify}
                        value={value}
                        placeholder={`${search.placeholder} (${search.tag})`}
                        aria-label={search.placeholder}
                        aria-describedby={pending ? 'file-cabinet-search-hint' : undefined}
                        disableLatin1Validation={true}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => change(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
                            if (fullText && e.key === 'Enter') {
                                store.setSearch(draft);
                            }
                        }}
                        onDelete={value ? clear : undefined}
                    />
                </S.Search>
                {fullText && (
                    <FileCabinetFtsBuilder
                        search={search}
                        query={draft}
                        onSearch={(query: string): void => {
                            setDraft(query);
                            store.setSearch(query);
                        }}
                    />
                )}
                {pending && <S.RecordCount id="file-cabinet-search-hint">Press Enter to search</S.RecordCount>}
            </S.SearchBlock>
        );
    },
);
